import React, { useState, useRef, useEffect, useCallback } from 'react';
import { searchCity, reverseGeocode } from '../../../api/citySearch';
import './SearchBar.css';

const STORAGE_KEY = 'selectedCity';

const SearchBar = ({ onSearch }) => {
  const [city, setCity] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [distance, setDistance] = useState(20);
  const [restrictToCity, setRestrictToCity] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const [selectedCity, setSelectedCity] = useState(null);
  const [suggestedCity, setSuggestedCity] = useState(null);
  const [geolocationAvailable, setGeolocationAvailable] = useState(false);
  const onSearchRef = useRef(onSearch);
  
  // Keep onSearch ref updated
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  useEffect(() => {
    const savedCity = localStorage.getItem(STORAGE_KEY);
    if (savedCity) {
      const parsedCity = JSON.parse(savedCity);
      setSelectedCity(parsedCity);
      setCity(parsedCity.cityName + (parsedCity.postalCode ? ` (${parsedCity.postalCode})` : ''));
      onSearchRef.current(parsedCity, 20, false); // Use default distance
    } else {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(async (position) => {
          if (!isMountedRef.current) return;
          setGeolocationAvailable(true);
          const { latitude, longitude } = position.coords;
          const foundCity = await reverseGeocode(latitude, longitude);
          if (!isMountedRef.current) return;
          if (foundCity) {
            setSuggestedCity(foundCity);
          }
        }, (error) => {
          console.log("Geolocation error:", error);
          if (isMountedRef.current) {
            setGeolocationAvailable(false);
          }
        });
      }
    }

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      isMountedRef.current = false;
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch(selectedCity, distance, restrictToCity);
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;
    setCity(inputValue);

    if (inputValue === '') {
      removeCitySelection();
      if (suggestedCity) {
        setSuggestions([{ ...suggestedCity, isLocation: true }]);
      } else {
        setSuggestions([]);
      }
      onSearch(null, distance, false);
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      fetchSuggestions(inputValue);
    }, 500);
  };

  const fetchSuggestions = useCallback(async (query) => {
    if (query.length > 2) {
      if (!isMountedRef.current) return;
      setLoading(true);
      try {
        const citySuggestions = await searchCity(query);
        if (!isMountedRef.current) return;
        setSuggestions(citySuggestions);
      } catch (error) {
        if (isMountedRef.current) {
          console.error('Error fetching suggestions:', error);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    } else {
      if (isMountedRef.current) {
        setSuggestions([]);
      }
    }
  }, []);

  const handleCitySelect = (selection) => {
    setSelectedCity(selection);
    setSuggestedCity(null);
    setCity(selection.cityName + (selection.postalCode ? ` (${selection.postalCode})` : ''));
    setSuggestions([]);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
    onSearch(selection, distance, restrictToCity);
  };

  const handleMouseEnter = (index) => {
    setActiveSuggestionIndex(index);
  };

  const handleMouseLeave = () => {
    setActiveSuggestionIndex(-1);
  };

  const removeCitySelection = () => {
    setSelectedCity(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const handleClearCity = () => {
    setCity('');
    setRestrictToCity(false);
    removeCitySelection();
    if (suggestedCity) {
      setSuggestions([{ ...suggestedCity, isLocation: true }]);
    } else {
      setSuggestions([]);
    }
    onSearch(null, distance, false);
  };

  const handleInputFocus = () => {
    if (city === '' && suggestedCity) {
      setSuggestions([{ ...suggestedCity, isLocation: true }]);
    }
  };

  const handleLocateMe = async () => {
    if (!navigator.geolocation) {
      alert('La géolocalisation n\'est pas supportée par votre navigateur.');
      return;
    }
    
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        if (!isMountedRef.current) return;
        const { latitude, longitude } = position.coords;
        const foundCity = await reverseGeocode(latitude, longitude);
        if (!isMountedRef.current) return;
        if (foundCity) {
          handleCitySelect(foundCity);
        }
        if (isMountedRef.current) {
          setLoading(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        if (isMountedRef.current) {
          setLoading(false);
        }
        alert('Impossible de récupérer votre position. Veuillez vérifier vos paramètres de localisation.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleDistancePreset = (value) => {
    setDistance(value);
    if (restrictToCity) {
      setRestrictToCity(false);
    }
    onSearch(selectedCity, value, false);
  };

  const handleRestrictToCityToggle = () => {
    if (!selectedCity) return;
    const nextValue = !restrictToCity;
    setRestrictToCity(nextValue);
    if (nextValue) {
      setDistance(null);
      onSearch(selectedCity, null, true);
      return;
    }

    const fallbackDistance = distance || 20;
    setDistance(fallbackDistance);
    onSearch(selectedCity, fallbackDistance, false);
  };

  return (
    <div className="search-bar-container">
      <form className="search-bar-form" onSubmit={handleSubmit}>
        <div className="search-bar-row">
          <div className="search-input-wrapper" ref={wrapperRef}>
            <input
              type="text"
              name="city"
              value={city}
              onChange={handleChange}
              onFocus={handleInputFocus}
              placeholder="Rechercher une ville..."
              id="search-location"
              ref={inputRef}
              autoComplete="off"
              className="search-input"
              style={{ paddingRight: city ? (geolocationAvailable ? '80px' : '40px') : (geolocationAvailable ? '50px' : '15px') }}
            />
            <div className="search-input-actions">
              {city && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={handleClearCity}
                  title="Effacer la ville"
                >
                  ×
                </button>
              )}
              {geolocationAvailable && (
                <button
                  type="button"
                  className="search-locate-btn"
                  onClick={handleLocateMe}
                  title="Me localiser"
                  disabled={loading}
                >
                  <i className="bi bi-geo-alt-fill"></i>
                </button>
              )}
            </div>
            {loading && <div className="search-loading">Chargement...</div>}
            {suggestions.length > 0 && !loading && (
              <ul className="search-suggestions-list">
                {suggestions.map((suggestion, index) => (
                  <li
                    className={`search-suggestion-item ${index === activeSuggestionIndex ? 'active' : ''}`}
                    key={index}
                    onClick={() => handleCitySelect(suggestion)}
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {suggestion.isLocation && <i className="bi bi-geo-alt-fill me-2 text-danger"></i>}
                    <div>
                      {suggestion.cityName} {suggestion.postalCode && `(${suggestion.postalCode})`}
                      {suggestion.isLocation && <small className="ms-2">- Ma position</small>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          
          <div className="distance-presets-inline">
            {[10, 20, 30, 50].map((preset) => (
              <button
                key={preset}
                type="button"
                className={`distance-preset-btn-inline ${!restrictToCity && distance === preset ? 'active' : ''}`}
                onClick={() => handleDistancePreset(preset)}
              >
                {preset} km
              </button>
            ))}
          </div>

          <button
            type="button"
            className={`restrict-city-btn ${restrictToCity ? 'active' : ''}`}
            onClick={handleRestrictToCityToggle}
            disabled={!selectedCity}
            aria-pressed={restrictToCity}
            title={!selectedCity ? 'Sélectionnez une ville pour activer ce filtre' : 'Afficher uniquement les événements de la ville sélectionnée'}
          >
            Ville exacte
          </button>
        </div>
      </form>
    </div>
  );
};

export default SearchBar;
